// ============================================================
// RACK - the pure list algebra behind the Armoury.
//
// The Armoury used to be forty fixed cells, so "reorder" meant swapping the
// contents of two of them and "delete" meant blanking one. A rack is an
// ordered list instead: moving a weapon INSERTS it at its destination and
// everything between shifts by one, and deleting splices.
//
// Every function here is pure and returns a new array only when something
// actually changed. `Slot.id` is the plate's POSITION, so these renumber it;
// weapon identity lives on `Slot.weaponId` and is never touched.
//
// The UI half of this lives in hooks/useSlotManagement.js, which is only
// `setAtkProfiles` plumbing on top of these.
// ============================================================
import type { Slot } from '../../types';
import { parseWS, wsLabel } from '../weaponSpecials';

/** True for an index that actually addresses a plate. */
function inRange(slots: Slot[], i: number): boolean {
  return Number.isInteger(i) && i >= 0 && i < slots.length;
}

/**
 * Renumber `id` to match position.
 *
 * Returns the SAME slot object for any plate already at its index, and the
 * same array when none needed changing. That identity is load-bearing:
 * `WeaponPlate` is memoised on the slot, so cloning untouched plates would
 * re-render the whole rack on every reorder.
 */
export function reindex(slots: Slot[]): Slot[] {
  if (slots.every((s, i) => s.id === i)) return slots;
  return slots.map((s, i) => (s.id === i ? s : { ...s, id: i }));
}

/**
 * Move the plate at `from` so that it ends up AT index `to`.
 *
 * Insert semantics, not swap: the plates in between shift by one. An
 * out-of-range index is left alone rather than dropping a weapon on the floor.
 */
export function moveSlot(slots: Slot[], from: number, to: number): Slot[] {
  if (from === to || !inRange(slots, from) || !inRange(slots, to)) return slots;
  const next = [...slots];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return reindex(next);
}

/** Insert `slot` at `at`, clamping past-the-end to an append. */
export function insertSlot(slots: Slot[], at: number, slot: Slot): Slot[] {
  const index = Math.max(0, Math.min(Number.isInteger(at) ? at : slots.length, slots.length));
  const next = [...slots];
  next.splice(index, 0, slot);
  return reindex(next);
}

/** Splice the plate at `at` out. No empty placeholder is left behind. */
export function removeSlot(slots: Slot[], at: number): Slot[] {
  if (!inRange(slots, at)) return slots;
  const next = [...slots];
  next.splice(at, 1);
  return reindex(next);
}

/** What happened to the rack, in the terms `remapSelection` needs. */
export type RackEdit =
  { kind: 'move'; from: number; to: number } | { kind: 'remove'; from: number } | { kind: 'insert'; to: number };

/**
 * Where the armed weapon ended up after an edit.
 *
 * The armed slot is an index (`selectedSlot`), and the ATTACK footer stays
 * live whatever it points at - so an index left un-remapped means firing a
 * weapon nobody chose. Removing the armed plate CLEARS the selection rather
 * than sliding it onto the neighbour, for the same reason.
 */
export function remapSelection(selected: number | null, edit: RackEdit): number | null {
  if (selected === null) return null;
  if (edit.kind === 'insert') return selected >= edit.to ? selected + 1 : selected;
  if (edit.kind === 'remove') return afterRemove(selected, edit.from);
  return afterMove(selected, edit.from, edit.to);
}

/** Where a selection lands when the plate at `from` is removed - nowhere, if it was that plate. */
function afterRemove(selected: number, from: number): number | null {
  if (selected === from) return null;
  return selected > from ? selected - 1 : selected;
}

/** Where a selection lands when the plate at `from` moves to `to`. */
function afterMove(selected: number, from: number, to: number): number {
  if (selected === from) return to;
  // Downward move: everything it jumped over slides up one.
  if (from < to) return selected > from && selected <= to ? selected - 1 : selected;
  // Upward move: everything it landed in front of slides down one.
  return selected >= to && selected < from ? selected + 1 : selected;
}

// ────────────────────────────────────────────────────────────
// Finding one weapon among forty
// ────────────────────────────────────────────────────────────

export interface RackFilter {
  /** Matched against the weapon's name AND its special rules. */
  query?: string;
  type?: 'ranged' | 'melee' | null;
}

/** A plate, and where it actually sits in the unfiltered rack. */
export interface RackHit {
  slot: Slot;
  index: number;
}

/**
 * Whether the rack is showing less than all of itself.
 *
 * Reorder is disabled while this is true: "move up" means nothing in a
 * view that is not showing the whole order, and the order is mechanically
 * load-bearing - the first non-Unwieldy melee weapon is what parries.
 */
export function isFiltering(filter: RackFilter): boolean {
  return !!filter.query?.trim() || !!filter.type;
}

/** Everything a query could reasonably be looking for on one weapon. */
function haystack(slot: Slot): string {
  const specials = (slot.specialRules ?? []).flatMap((rule) => {
    const { id } = parseWS(rule);
    // Both spellings: the chip reads "Power Field", the data says
    // "PowerField", and nobody is going to type the second one.
    return [rule, wsLabel(id)];
  });
  return [slot.weaponName, ...specials].join(' ').toLowerCase();
}

/**
 * The rack, narrowed - carrying each plate's ORIGINAL position.
 *
 * That position is not a detail. `selectedSlot`, `handleMoveSlot`,
 * `handleDeleteSlot` and the `1`-`9` keys all speak rack positions, so a
 * filtered view that renumbered from zero would arm, move and delete the
 * wrong weapons.
 */
export function filterRack(slots: Slot[], filter: RackFilter): RackHit[] {
  const query = filter.query?.trim().toLowerCase() ?? '';
  return slots
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => {
      if (filter.type && slot.weaponType !== filter.type) return false;
      return !query || haystack(slot).includes(query);
    });
}

/**
 * Which rack position a saved macro should arm.
 *
 * By WEAPON first: a macro used to remember the cell it fired from, so
 * reordering the rack silently re-pointed it at a different gun. Absent
 * `weaponId` means a macro saved before v8.191.0 and falls back to the
 * stored position, which is exactly the old behaviour.
 *
 * Null rather than an out-of-range index - the ATTACK footer stays live
 * whatever `selectedSlot` holds, so arming a position that does not exist
 * is worse than arming nothing.
 */
export function macroWeaponIndex(macro: { weaponId?: string; slotIndex?: number }, slots: Slot[]): number | null {
  if (macro.weaponId) {
    const found = slots.findIndex((s) => s.weaponId === macro.weaponId);
    if (found >= 0) return found;
  }
  const fallback = macro.slotIndex;
  return typeof fallback === 'number' && fallback >= 0 && fallback < slots.length ? fallback : null;
}
